import React, { Dispatch, createContext, useContext, useEffect, useReducer } from 'react';
import * as P from "../program-state"
import { z } from "zod";


export interface StepData {
    name: string;
    id: string;
    order: number;
    substeps: Substep[];
}

type status_t = "complete" | "incomplete" | "inprogress";

export interface Substep {
    name: string;
    id: string;
    status: status_t;
    data?: object | undefined;
}


export interface Onboarding_context_type {
  current_step: string;
  steps: StepData[];
}

export type Wizard_context_type = Onboarding_context_type

// @TODO: reuse this for type
const SubstepSchema = z.object({
  name: z.string(),
  id: z.string(),
  status: z.enum(["complete", "incomplete", "inprogress"]),
  data: z.any().nullish()
});

const StepDataSchema = z.object({
  name: z.string(),
  id: z.string(),
  order: z.number(),
  substeps: z.array(SubstepSchema)
});

const OnboardingContextTypeSchema = z.object({
  current_step: z.string(),
  steps: z.array(StepDataSchema)
});

const WizardContextTypeSchema = OnboardingContextTypeSchema

const OnboardingContext = createContext<[Onboarding_context_type | undefined, Dispatch<any>] |undefined>([undefined, () => {}]);

const WizardContext = OnboardingContext


const generate_substep_id_list = (step_data: StepData[]) => {
  let step_ids : string[] = []
  step_data.forEach((step) => { step.substeps.forEach((substep) => { step_ids.push(substep.id) }) });
  return step_ids;
}


const generate_substep_id_and_status_list = (step_data: StepData[]) => {
  let step_ids : { id : string, status : status_t }[] = []
  step_data.forEach((step) => { step.substeps.forEach((substep) => { step_ids.push({id: substep.id, status: substep.status}) }) });
  return step_ids;
}


const onboardingReducer = (state: Onboarding_context_type, action: { type: string , payload ?: any }) => {
  switch (action.type) {
    case 'SET_STATE': {
      return action.payload;
    }
    case 'NEXT_STEP': {
        // @TODO: refactor this to precompute the array and iterate only
        let step_ids = generate_substep_id_list(state.steps);
        const current_index = step_ids.indexOf(state.current_step);
        let next_index = current_index + 1;
        next_index = next_index >= step_ids.length ? 0 : next_index;
        const next_step_id = step_ids[next_index];
        return {
          ...state,
          current_step: next_step_id
        }
    }
    case 'PREVIOUS_STEP': {
        // @TODO: same with above
        let step_ids = generate_substep_id_list(state.steps);
        const current_index = step_ids.indexOf(state.current_step);
        let previous_index = current_index - 1;
        previous_index = previous_index < 0 ? step_ids.length - 1 : previous_index;
        const previous_step_id = step_ids[previous_index];
        return {
          ...state,
          current_step: previous_step_id
        }
    }
    case 'SET_ACTIVE_STEP': {
      return {
        ...state,
        current_step: action.payload
      }
    }
    case 'MARK_STEP_COMPLETE': {
      let step_id = action?.payload?.step_id;
      return {
        ...state,
        steps: state.steps.map(step =>
          step.id === step_id
            ? {
                ...step,
                substeps: step.substeps.map(substep =>
                  substep.id === action.payload.substep_id
                    ? { ...substep, status: "complete" }
                    : substep
                )
              }
            : step
        )
      };
    }
    case 'MARK_STEP_INCOMPLETE': {
      let step_id = action.payload.step_id;
      return {
        ...state,
        steps: state.steps.map(step =>
          step.id === step_id
            ? {
                ...step,
                substeps: step.substeps.map(substep =>
                  substep.id === action.payload.substep_id
                    ? { ...substep, status: "incomplete" }
                    : substep
                )
              }
            : step
        )
      };
    }
    case 'MARK_STEP_INPROGRESS': {
      return state
    }
    case 'SET_TO_REQUIRED_STEP': {
      // @TODO: refactor this
      let step_and_status_list = generate_substep_id_and_status_list(state.steps);
      // find first completed step
      let first_completed_step = step_and_status_list.find((step) => step.status === "complete");
      if (!first_completed_step) {
        // if no completed step, set the first step as active
        return {
          ...state,
          current_step: step_and_status_list[0].id
        }
      }
      // set current step to the next step
      let current_index = step_and_status_list.indexOf(first_completed_step);
      let next_index = current_index + 1;
      next_index = next_index >= step_and_status_list.length ? 0 : next_index;
      const next_step_id = step_and_status_list[next_index].id;
      return {
        ...state,
        current_step: next_step_id
      }
    }
    case 'SET_STEP_DATA': {
      // @TODO: refactor the getter function for step mutation
      let step_id = action.payload.step_id;
      let step = state.steps.find((step) => step.id === step_id);
      if (step) {
        let subs = step.substeps;
        let substep = subs.find((substep) => substep.id === action.payload.substep_id);
        if (substep) {
          substep.data = action.payload.data
        }
      }
      return state;
    }
    default: {
        throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const step_data : StepData[] = [
    {
        name: "Oauth integration and social network syncing",
        order: 1,
        id: "step1",
        substeps: [
            {
                name: "Oauth integration",
                id: "substep11",
                status: "incomplete"
            },
            {
                name: "Asset syncing and verification",
                id: "substep12",
                status: "incomplete"
            }
        ]
    },
    {
        name: "Tracking pixels / tag installation",
        id: "step2",
        order: 2,
        substeps: [
            {
                name: "Metal pixel",
                id: "substep21",
                status: "incomplete",
            },
            {
                name: "Linkedin insight tag",
                id: "substep22",
                status: "incomplete",
            },
            {
                name: "Tiktok pixel",
                id: "substep23",
                status: "incomplete",
            }
        ]
    },
    {
      name: "Brand creation",
      id: "step3",
      order: 3,
      substeps: [
            {
                name: "Brand creation",
                id: "substep31",
                status: "incomplete",
            }
        ]
    },
    {
      name: "Audience creation",
      id: "step4",
      order: 4,
      substeps: [
          {
              name: "Create custom audience",
              id: "substep41",
              status: "incomplete",
          },
          {
              name: "Create saved audience",
              id: "substep42",
              status: "incomplete",
          }
      ]
    },
    {
        name: "First campaign creation",
        id: "step5",
        order: 5,
        substeps: [
            {
                name: "Create first campaign",
                id: "substep51",
                status: "incomplete",
            }
        ]
    }
]


const init_state: Onboarding_context_type = {
  current_step: "substep11",
  steps: step_data
};


export const OnboardingContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [ps, cs] = P.use_authenticated_program_state()

  const [state, dispatch] = useReducer(onboardingReducer, undefined);
  useEffect(() => {
    P.issue_json_get(P.v1api`user/me/wizard-state`, OnboardingContextTypeSchema , cs.token).then((response) => {
      const [rok, rval] = response
      if (rok) {
        dispatch({ type: 'SET_STATE', payload: rval });
        dispatch({ type: 'SET_TO_REQUIRED_STEP' });
      }
      else {
        dispatch({ type: 'SET_STATE', payload: init_state });
      }
    }).catch((error) => {
      console.error(`Could not get saved onboarding state: ${error}`);
    });
  }, []);

  return <OnboardingContext.Provider value={[state, dispatch]}>{children}</OnboardingContext.Provider>;
};

export const WizardContextProvider = OnboardingContextProvider

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingContextProvider');
  }
  return context;
};

export const useWizardContext = useOnboardingContext
